import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { SiFacebook, SiInstagram, SiLinkedin, SiX } from 'react-icons/si';
import { motion, AnimatePresence } from 'framer-motion';
import { ROUTE_PATHS } from '@/lib/index';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { path: ROUTE_PATHS.HOME, label: 'Home' },
    { path: ROUTE_PATHS.ABOUT, label: 'About' },
    { path: ROUTE_PATHS.SERVICES, label: 'Services' },
    { path: ROUTE_PATHS.FACILITIES, label: 'Facilities' },
    { path: ROUTE_PATHS.PROJECTS, label: 'Projects' },
    { path: ROUTE_PATHS.GALLERY, label: 'Gallery' },
    { path: ROUTE_PATHS.CONTACT, label: 'Contact' },
  ];

  const socialLinks = [
    { icon: SiFacebook, href: 'https://facebook.com', label: 'Facebook' },
    { icon: SiInstagram, href: 'https://instagram.com', label: 'Instagram' },
    { icon: SiX, href: 'https://twitter.com', label: 'X' },
    { icon: SiLinkedin, href: 'https://linkedin.com', label: 'LinkedIn' },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-background/80 backdrop-blur-xl border-b border-border shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <NavLink
              to={ROUTE_PATHS.HOME}
              className="flex items-center space-x-3 group"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full group-hover:bg-primary/30 transition-all duration-300" />
                <div className="relative w-12 h-12 bg-gradient-to-br from-primary via-primary/90 to-accent rounded-lg flex items-center justify-center shadow-lg">
                  <span className="text-2xl font-bold text-primary-foreground">B</span>
                </div>
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-bold text-foreground tracking-tight">
                  Bibia Films
                </span>
                <span className="text-xs text-muted-foreground tracking-wider">
                  International Studios
                </span>
              </div>
            </NavLink>

            <nav className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? 'bg-primary text-primary-foreground shadow-lg'
                        : 'text-foreground hover:bg-muted hover:text-foreground'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-muted transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6 text-foreground" />
              ) : (
                <Menu className="w-6 h-6 text-foreground" />
              )}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden border-t border-border bg-background/95 backdrop-blur-xl"
            >
              <nav className="container mx-auto px-4 py-4 flex flex-col space-y-2">
                {navItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={({ isActive }) =>
                      `px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? 'bg-primary text-primary-foreground shadow-lg'
                          : 'text-foreground hover:bg-muted'
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main className="flex-1 pt-20">{children}</main>

      <footer className="bg-gradient-to-b from-background to-muted border-t border-border">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-primary via-primary/90 to-accent rounded-lg flex items-center justify-center shadow-lg">
                  <span className="text-xl font-bold text-primary-foreground">B</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-lg font-bold text-foreground">
                    Bibia Films
                  </span>
                  <span className="text-xs text-muted-foreground">
                    International Studios
                  </span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Future-facing cinema that transcends borders, cultures, and languages.
                Creating globally relevant narratives with world-class talent.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Contact
              </h3>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>Mumbai, India</p>
                <p>
                  <a
                    href="mailto:Info@keshhavpanneriy.com"
                    className="hover:text-primary transition-colors"
                  >
                    Info@keshhavpanneriy.com
                  </a>
                </p>
                <p className="mt-4">
                  <span className="font-medium text-foreground">Founded by:</span>
                  <br />
                  Keshhav Panneriy
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Follow Us
              </h3>
              <div className="flex space-x-3">
                {socialLinks.map((social) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-lg bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-all duration-200 hover:scale-110 hover:shadow-lg"
                      aria-label={social.label}
                    >
                      <Icon className="w-5 h-5" />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-border">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-sm text-muted-foreground">
                © 2026 Bibia Films Private Limited. All rights reserved.
              </p>
              <div className="flex space-x-6 text-sm text-muted-foreground">
                <a href="#" className="hover:text-primary transition-colors">
                  Privacy Policy
                </a>
                <a href="#" className="hover:text-primary transition-colors">
                  Terms of Service
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}